import type {
  DaxPreviewRequest,
  DaxPreviewResponse,
  GraphViewModel,
  KPIConcept,
  ScanJob,
  ScopePreflight,
  WorkspaceScope,
} from '@metricgraph/contracts';
import type {
  AgentConfigurationView,
  KPIRecordData,
  MeasureView,
  Paginated,
  ReviewCaseView,
  SandboxView,
  SearchResult,
} from './api.js';

type DemoFile =
  | 'core.json'
  | 'graph.json'
  | 'kpi-records.json'
  | 'measures.json'
  | 'reviews.json'
  | 'search.json';

interface CoreData {
  session: unknown;
  overview: unknown;
  kpis: { items: KPIConcept[] };
  sandbox: { items: SandboxView[] };
  governance: unknown;
  capabilities: unknown;
  agentConfiguration: AgentConfigurationView;
  scopes: { items: WorkspaceScope[] };
  matrix: unknown;
  resolutionExamples: { items: Array<Record<string, unknown> & { inputName: string }> };
  preflight: ScopePreflight;
  scope: WorkspaceScope;
  scan: { item: ScanJob };
}

function stableHash(value: string): string {
  let hash = 2166136261;
  for (const character of value) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash >>> 0)
    .toString(16)
    .padStart(8, '0')
    .repeat(8);
}

function staticDaxPreview(input: DaxPreviewRequest, measures: MeasureView[]): DaxPreviewResponse {
  if (input.inputMode === 'NATURAL_LANGUAGE') {
    throw new Error(
      'Natural-language DAX generation requires a configured server-side AI provider. Paste DAX to preview without one.',
    );
  }
  if (!input.confirmedModel)
    throw new Error('Confirm the selected semantic model before previewing DAX.');
  const source = measures.find(
    ({ id, semanticModelId }) =>
      id === input.sourceMeasureId && semanticModelId === input.semanticModelId,
  );
  if (!source) throw new Error('The selected measure is outside the confirmed semantic model.');
  const proposedName = `${source.name} preview`;
  const expression = input.input.trim();
  const daxQuery = /\b(EVALUATE|DEFINE)\b/i.test(expression)
    ? expression
    : `DEFINE\n  MEASURE '${source.tableName.replaceAll("'", "''")}'[${proposedName}] = ${expression}\nEVALUATE\n  ROW("${proposedName}", [${proposedName}])`;
  const forbidden =
    /\b(CREATE|ALTER|DROP|DELETE|INSERT|UPDATE|PROCESS|REFRESH|OPENROWSET|WEBSERVICE)\b/i.test(
      expression,
    );
  const allowedNames = new Set(
    measures
      .filter(({ semanticModelId }) => semanticModelId === source.semanticModelId)
      .map(({ name }) => name)
      .concat(proposedName),
  );
  const unknown = [
    ...new Set([...daxQuery.matchAll(/\[([^\]]+)\]/g)].map((match) => match[1]!.trim())),
  ].filter((name) => !allowedNames.has(name));
  const issues = [
    ...(forbidden
      ? [
          {
            code: 'DAX_WRITE_OPERATION',
            severity: 'ERROR' as const,
            message: 'Write or external-access DAX is not allowed.',
          },
        ]
      : []),
    ...unknown.map((name) => ({
      code: 'UNKNOWN_MODEL_OBJECT',
      severity: 'ERROR' as const,
      message: `The DAX references an object outside the selected model: [${name}].`,
    })),
  ];
  const resultHash = stableHash(daxQuery);
  const seed = Number.parseInt(resultHash.slice(0, 8), 16);
  const baseline = 850_000 + (seed % 300_000);
  const proposed = Math.round(baseline * (1 + ((seed % 1801) - 900) / 10_000));
  const failed = issues.some(({ severity }) => severity === 'ERROR');
  return {
    inputMode: 'DAX',
    generatedBy: 'USER',
    providerKind: 'DISABLED',
    sourceMeasureId: source.id,
    semanticModelId: source.semanticModelId,
    proposedName,
    measureExpression: expression,
    daxQuery,
    explanation: 'User-supplied DAX retained for governed validation and preview.',
    assumptions: ['The selected semantic model and source measure are confirmed by the user.'],
    confidence: 1,
    validation: {
      state: failed ? 'FAILED' : 'PASSED',
      issues,
      referencedObjects: [
        ...new Set([...daxQuery.matchAll(/\[([^\]]+)\]/g)].map((match) => match[1]!.trim())),
      ].toSorted(),
    },
    execution: failed
      ? {
          state: 'NOT_EXECUTED',
          evidenceBoundary: 'CONFIGURATION_ONLY',
          columns: [],
          rows: [],
          resultHash: null,
          expiresAt: null,
        }
      : {
          state: 'SYNTHETIC_PREVIEW',
          evidenceBoundary: 'SYNTHETIC',
          columns: [
            { name: 'Definition', dataType: 'STRING' },
            { name: 'Illustrative value', dataType: 'NUMBER' },
          ],
          rows: [
            { Definition: source.name, 'Illustrative value': baseline },
            { Definition: proposedName, 'Illustrative value': proposed },
          ],
          resultHash,
          expiresAt: new Date(Date.now() + 15 * 60_000).toISOString(),
        },
    provenance: {
      contextVersion: 'metricgraph-context-1.0.0',
      instructionVersion: 'metricgraph-dax-draft-1.0.0',
      sourceSnapshotAt: source.provenance.lastObservedAt,
      warnings: [
        failed
          ? 'DAX failed validation and was not executed.'
          : 'Illustrative synthetic values are not live semantic-model query results.',
        'No production semantic model or report was modified.',
      ],
    },
  };
}

const fileCache = new Map<DemoFile, Promise<unknown>>();
const decisions = new Map<string, { decision: string; decidedAt: string }>();
const sandboxOverrides = new Map<string, SandboxView>();
let scanPoll = 0;

function loadFile<T>(name: DemoFile): Promise<T> {
  const cached = fileCache.get(name);
  if (cached) return cached as Promise<T>;
  const base = import.meta.env.BASE_URL.endsWith('/')
    ? import.meta.env.BASE_URL
    : `${import.meta.env.BASE_URL}/`;
  const pending = fetch(`${base}demo-data/${name}`).then(async (response) => {
    if (!response.ok) throw new Error(`Static demo data could not be loaded (${response.status}).`);
    return response.json() as Promise<T>;
  });
  fileCache.set(name, pending);
  return pending;
}

function body<T>(init?: RequestInit): T {
  return JSON.parse(typeof init?.body === 'string' ? init.body : '{}') as T;
}

function paginate<T>(items: T[], params: URLSearchParams): Paginated<T> {
  const page = Math.max(1, Number(params.get('page') ?? 1));
  const pageSize = Math.min(100, Math.max(1, Number(params.get('pageSize') ?? 25)));
  const offset = (page - 1) * pageSize;
  return {
    items: items.slice(offset, offset + pageSize),
    page,
    pageSize,
    total: items.length,
    totalPages: Math.max(1, Math.ceil(items.length / pageSize)),
  };
}

function scanProgress(template: ScanJob): ScanJob {
  const steps: Array<[ScanJob['stage'], number]> = [
    ['RESOLVING_SCOPE', 8],
    ['INVENTORYING_ITEMS', 28],
    ['READING_SEMANTIC_METADATA', 48],
    ['ENRICHING_REPORT_USAGE', 68],
    ['PROJECTING_GRAPH', 82],
    ['CLASSIFYING_MEASURES', 92],
    ['CREATING_REVIEW_CASES', 97],
    ['COMPLETE', 100],
  ];
  const [stage, progress] = steps[Math.min(scanPoll, steps.length - 1)]!;
  scanPoll += 1;
  return {
    ...template,
    id: 'scan-static-demo',
    status: stage === 'COMPLETE' ? 'COMPLETED_WITH_GAPS' : 'RUNNING',
    stage,
    progress,
    updatedAt: new Date().toISOString(),
    completedAt: stage === 'COMPLETE' ? new Date().toISOString() : null,
  };
}

export async function staticDemoApi<T>(path: string, init?: RequestInit): Promise<T> {
  const url = new URL(path, window.location.origin);
  const core = await loadFile<CoreData>('core.json');

  if (url.pathname === '/api/v1/session') return core.session as T;
  if (url.pathname === '/api/v1/overview') return core.overview as T;
  if (url.pathname === '/api/v1/kpis') return core.kpis as T;
  if (url.pathname === '/api/v1/governance-health') return core.governance as T;
  if (url.pathname === '/api/v1/connections/capabilities') return core.capabilities as T;
  if (url.pathname === '/api/v1/agent/configuration') return core.agentConfiguration as T;
  if (url.pathname === '/api/v1/scopes' && !init?.method) return core.scopes as T;
  if (url.pathname === '/api/v1/matrix') return core.matrix as T;

  const kpiMatch = /^\/api\/v1\/kpis\/([^/]+)$/.exec(url.pathname);
  if (kpiMatch?.[1]) {
    const records = await loadFile<Record<string, KPIRecordData>>('kpi-records.json');
    const record = records[decodeURIComponent(kpiMatch[1])];
    if (!record) throw new Error('KPI not found in the representative demo sample.');
    return record as T;
  }

  if (url.pathname === '/api/v1/measures') {
    const data = await loadFile<{ items: MeasureView[] }>('measures.json');
    const query = (url.searchParams.get('q') ?? '').trim().toLocaleLowerCase();
    const classification = url.searchParams.get('classification');
    const items = data.items
      .filter((item) =>
        query
          ? `${item.name} ${item.tableName} ${item.modelName ?? ''}`
              .toLocaleLowerCase()
              .includes(query)
          : true,
      )
      .filter((item) =>
        classification ? item.classification?.classification === classification : true,
      );
    return paginate(items, url.searchParams) as T;
  }

  if (url.pathname === '/api/v1/agent/dax/preview') {
    const data = await loadFile<{ items: MeasureView[] }>('measures.json');
    return staticDaxPreview(body<DaxPreviewRequest>(init), data.items) as T;
  }

  if (url.pathname === '/api/v1/review-cases') {
    const data = await loadFile<{ items: ReviewCaseView[] }>('reviews.json');
    const triage = url.searchParams.get('triage');
    const items = data.items
      .filter((item) => (triage ? item.triageType === triage : true))
      .map((item) => {
        const override = decisions.get(item.id);
        return override
          ? { ...item, ...override, decidedBy: 'static-demo-user', updatedAt: override.decidedAt }
          : item;
      });
    return paginate(items, url.searchParams) as T;
  }

  if (url.pathname === '/api/v1/search') {
    const data = await loadFile<{ items: SearchResult[] }>('search.json');
    const query = (url.searchParams.get('q') ?? '').trim().toLocaleLowerCase();
    return {
      items: data.items
        .filter((item) => `${item.label} ${item.context}`.toLocaleLowerCase().includes(query))
        .slice(0, 60),
    } as T;
  }

  if (url.pathname === '/api/v1/sandbox' && !init?.method) {
    return {
      items: core.sandbox.items.map((item) => sandboxOverrides.get(item.id) ?? item),
    } as T;
  }

  const graphMatch = /^\/api\/v1\/graph\/([^/]+)$/.exec(url.pathname);
  if (graphMatch) {
    const graphs = await loadFile<Record<string, GraphViewModel>>('graph.json');
    const key = `${url.searchParams.get('lens') ?? 'BUSINESS'}:${url.searchParams.get('depth') ?? '2'}:${url.searchParams.get('direction') ?? 'BOTH'}`;
    const graph = graphs[key] ?? graphs['BUSINESS:2:BOTH'];
    if (!graph) throw new Error('The static graph snapshot is unavailable.');
    const requestedFocus = decodeURIComponent(graphMatch[1] ?? graph.focusNodeId);
    return {
      ...graph,
      focusNodeId: graph.nodes.some(({ id }) => id === requestedFocus)
        ? requestedFocus
        : graph.focusNodeId,
    } as T;
  }

  if (url.pathname === '/api/v1/connections/fabric/resolve') {
    const { names = [] } = body<{ names?: string[] }>(init);
    return {
      items: names.map((inputName) => {
        const example = core.resolutionExamples.items.find(
          (item) => item.inputName.toLocaleLowerCase() === inputName.toLocaleLowerCase(),
        );
        return (
          example ?? {
            inputName,
            state: 'NOT_FOUND',
            workspaceId: null,
            displayName: null,
            candidates: [],
          }
        );
      }),
    } as T;
  }

  if (url.pathname === '/api/v1/connections/fabric/preflight') return core.preflight as T;
  if (url.pathname === '/api/v1/scopes' && init?.method === 'POST') {
    return {
      item: {
        ...core.scope,
        id: 'scope-static-demo',
        name: body<{ name?: string }>(init).name ?? core.scope.name,
        updatedAt: new Date().toISOString(),
      },
    } as T;
  }
  if (/^\/api\/v1\/scopes\/[^/]+\/scans$/.test(url.pathname)) {
    scanPoll = 0;
    return { item: scanProgress(core.scan.item) } as T;
  }
  if (/^\/api\/v1\/scans\/[^/]+$/.test(url.pathname)) {
    return { item: scanProgress(core.scan.item) } as T;
  }

  const decisionMatch = /^\/api\/v1\/review-cases\/(.+)\/decision$/.exec(url.pathname);
  if (decisionMatch?.[1]) {
    const input = body<{ decision?: string }>(init);
    const decidedAt = new Date().toISOString();
    const id = decodeURIComponent(decisionMatch[1]);
    decisions.set(id, { decision: input.decision ?? 'APPROVED', decidedAt });
    return { item: { id, decision: input.decision ?? 'APPROVED' } } as T;
  }

  if (/^\/api\/v1\/review-cases\/.+\/promote-draft$/.test(url.pathname)) {
    return {
      draft: {
        concept: core.kpis.items[0],
        contractVersion: {},
        inheritedEndorsement: false,
        sourceReviewCaseId: 'static-demo',
      },
      rationale: 'Created only in this browser session.',
    } as T;
  }

  if (url.pathname === '/api/v1/sandbox/promote') {
    const input = body<{ sandboxMetricId?: string; rationale?: string }>(init);
    const item = core.sandbox.items.find(({ id }) => id === input.sandboxMetricId);
    return { draft: item, rationale: input.rationale ?? '' } as T;
  }

  const closeMatch = /^\/api\/v1\/sandbox\/([^/]+)\/(archive|dispose)$/.exec(url.pathname);
  if (closeMatch?.[1] && closeMatch[2]) {
    const id = decodeURIComponent(closeMatch[1]);
    const current = sandboxOverrides.get(id) ?? core.sandbox.items.find((item) => item.id === id);
    if (!current) throw new Error('Sandbox item not found in the static demo.');
    const item: SandboxView = {
      ...current,
      lifecycle: closeMatch[2] === 'archive' ? 'ARCHIVED' : 'DISPOSED',
      updatedAt: new Date().toISOString(),
    };
    sandboxOverrides.set(id, item);
    return { item } as T;
  }

  throw new Error(`The static demo does not provide ${url.pathname}.`);
}
